import { useCopilotAction } from '@copilotkit/react-core';
import { useNavigate } from 'react-router-dom';

import { useAPI } from 'hooks/api/useAPI';

type NavigationTarget =
  | 'templates_gallery'
  | 'model_gallery'
  | 'pipelines'
  | 'pipeline_detail'
  | 'gateways'
  | 'gateway_detail'
  | 'cameras'
  | 'camera_detail'
  | 'streams'
  | 'stream_detail'
  | 'files'
  | 'file_detail'
  | 'deploy_new'
  | 'search_recordings'
  | 'dashboard';

const NAVIGATION_TARGETS: Record<
  NavigationTarget,
  { path: string; description: string; requiresId?: string }
> = {
  templates_gallery: {
    path: '/design/solutions',
    description: 'Pipeline templates gallery',
  },
  model_gallery: {
    path: '/design/models',
    description: 'AI models gallery',
  },
  pipelines: {
    path: '/design/pipelines',
    description: 'Your custom pipelines/workflows',
  },
  pipeline_detail: {
    path: '/design/pipelines/:id',
    description: 'Edit a specific pipeline in the workflow editor',
    requiresId: 'pipelineId',
  },
  gateways: {
    path: '/deploy/gateways',
    description: 'List of all gateways',
  },
  gateway_detail: {
    path: '/deploy/gateways/:id',
    description:
      'Gateway detail view with cameras, workflows, status, hardware details',
    requiresId: 'gatewayId',
  },
  cameras: {
    path: '/deploy/cameras',
    description: 'List of all cameras',
  },
  camera_detail: {
    path: '/deploy/cameras/:id',
    description: 'Camera detail view with workflows, streams, settings',
    requiresId: 'cameraId',
  },
  streams: {
    path: '/deploy/streams',
    description: 'List of all RTSP streams not associated with a camera',
  },
  stream_detail: {
    path: '/deploy/streams/:id',
    description: 'Stream detail view with workflows, shared streams, settings',
    requiresId: 'streamId',
  },
  files: {
    path: '/deploy/files',
    description: 'List of user-uploaded file streams',
  },
  file_detail: {
    path: '/deploy/files/:id',
    description: 'File stream detail view with associated workflows',
    requiresId: 'fileStreamId',
  },
  deploy_new: {
    path: '/deploy/new',
    description: 'Deploy a workflow to a video source',
  },
  search_recordings: {
    path: '/monitor/search',
    description: 'Search recordings saved by workflows',
  },
  dashboard: {
    path: '/monitor/dashboards/:id',
    description: 'View a specific dashboard',
    requiresId: 'dashboardId',
  },
};

export function useNavigateConsoleTool() {
  const { applicationID } = useAPI();
  const navigate = useNavigate();

  useCopilotAction({
    name: 'navigateConsole',
    description: `Navigate the user to a specific part of the Lumeo console. Use this tool when it would help the user to be guided to a specific part of the console in response to a question or request. Available targets: ${Object.entries(
      NAVIGATION_TARGETS
    )
      .map(([key, { description }]) => `"${key}" (${description})`)
      .join(', ')}`,
    parameters: [
      {
        name: 'target',
        type: 'string',
        description: `The navigation target. One of: ${Object.keys(NAVIGATION_TARGETS).join(', ')}`,
        required: true,
      },
      {
        name: 'pipelineId',
        type: 'string',
        description: 'Pipeline ID (required for pipeline_detail target)',
        required: false,
      },
      {
        name: 'gatewayId',
        type: 'string',
        description: 'Gateway ID (required for gateway_detail target)',
        required: false,
      },
      {
        name: 'cameraId',
        type: 'string',
        description: 'Camera ID (required for camera_detail target)',
        required: false,
      },
      {
        name: 'streamId',
        type: 'string',
        description: 'Stream ID (required for stream_detail target)',
        required: false,
      },
      {
        name: 'fileStreamId',
        type: 'string',
        description: 'File stream ID (required for file_detail target)',
        required: false,
      },
      {
        name: 'dashboardId',
        type: 'string',
        description: 'Dashboard ID (required for dashboard target)',
        required: false,
      },
    ],
    handler: async (args: {
      target: string;
      pipelineId?: string;
      gatewayId?: string;
      cameraId?: string;
      streamId?: string;
      fileStreamId?: string;
      dashboardId?: string;
    }) => {
      const target = args.target as NavigationTarget;
      const config = NAVIGATION_TARGETS[target];

      if (!config) {
        return {
          success: false,
          message: `Unknown navigation target: ${args.target}. Valid targets are: ${Object.keys(NAVIGATION_TARGETS).join(', ')}`,
        };
      }

      let path = config.path;

      if (config.requiresId) {
        const idMap: Record<string, string | undefined> = {
          pipelineId: args.pipelineId,
          gatewayId: args.gatewayId,
          cameraId: args.cameraId,
          streamId: args.streamId,
          fileStreamId: args.fileStreamId,
          dashboardId: args.dashboardId,
        };

        const id = idMap[config.requiresId];

        if (!id) {
          return {
            success: false,
            message: `The "${target}" target requires a ${config.requiresId} parameter`,
          };
        }

        path = path.replace(':id', id);
      }

      const fullPath = `/applications/${applicationID}${path}`;

      navigate(fullPath);

      return {
        success: true,
        message: `Navigated to ${config.description}`,
        path: fullPath,
      };
    },
  });
}
